import {
  Flex,
  Icon,
  Input,
  LoadingSpinner,
  Text,
} from '@sendoutcards/quantum-design-ui'
import React from 'react'
import styles from './membershipRewardsConfigurator.module.scss'
import { Button } from 'src/design_system/components/Button/Button'
import { useSelector, useState } from 'src/hooks'
import HoverTooltip from 'src/chrome/HoverTooltip/HoverTooltip'

function clamp(min: number, max: number, value: number) {
  return Math.min(max, Math.max(min, value))
}

export interface MembershipRewardsConfiguratorProps {
  totalMRAvailable: number
  maxUsableMRs: number
  currentMR: number
  onMRUpdate: (newRPAmount: number) => void
  onCancel: () => void
}

const MembershipRewardsConfigurator: React.FC<
  MembershipRewardsConfiguratorProps
> = props => {
  const { totalMRAvailable, maxUsableMRs, currentMR, onMRUpdate, onCancel } =
    props

  const { width } = useSelector(state => state.window)
  const isMobile = width <= 542
  const [rawValue, setRawValue] = useState<string>(
    currentMR !== 0 ? currentMR.toString() : '50',
  )
  const [amount, setAmount] = useState<number>(currentMR !== 0 ? currentMR : 50)
  const [isApplyDisabled, setIsApplyDisabled] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [warningMessage, setWarningMessage] = useState<string | undefined>(
    undefined,
  )

  const onInputChange = (value: string) => {
    const numericValue = Number(value)
    if (!Number.isNaN(numericValue)) {
      const clamped = clamp(0, maxUsableMRs, numericValue)
      if (clamped < 50 && clamped > 0) {
        setIsApplyDisabled(true)
        setWarningMessage('Membership Rewards must be at least 50.')
      } else {
        if (isApplyDisabled) setIsApplyDisabled(false)
        if (warningMessage) setWarningMessage(undefined)
      }
      setAmount(clamped)
      setRawValue(clamped.toString())
    } else {
      setAmount(0)
      setRawValue('0')
    }
  }

  return (
    <Flex
      flexDirection="column"
      width="100%"
      style={{
        padding: '1.25rem',
        background: 'linear-gradient(135deg, #d4f0ef, #f9dde6)',
        borderRadius: '12px',
      }}
    >
      <Text
        type="body"
        content="Apply Membership Rewards to this order"
        weight="bold"
        style={{ color: '#404040', fontSize: '1.2rem' }}
      />
      <Flex flexDirection="column" width="100%" outset={{ top: 'x2' }}>
        <Flex flexDirection="row" justifyContent="space-between" width="100%">
          <Text type="body" content="Total Membership Rewards Available" />
          <Text
            type="body"
            content={totalMRAvailable.toLocaleString()}
            weight="bold"
          />
        </Flex>
        <Flex flexDirection="row" justifyContent="space-between" width="100%">
          <Flex alignItems="center" justifyContent="center">
            <Text type="body" content="Order limit:" />
            <HoverTooltip
              title="Membership Rewards can be used for cards and gifts. Not valid for Gift Cards or shipping"
              tooltipChildren={
                <div style={isMobile ? { width: '190px' } : undefined}>
                  <Text
                    type="footnote"
                    color="accent"
                    whiteSpace={isMobile ? 'normal' : 'pre'}
                  >
                    Membership Rewards can be used for cards and gifts. Not
                    valid for Gift Cards or shipping
                  </Text>
                </div>
              }
              shouldSupportOnTapAsHover={true}
              background="dark"
            >
              <Flex>
                <Icon
                  name="information"
                  size="xSmall"
                  primaryColor="primaryBodyText"
                />
              </Flex>
            </HoverTooltip>
          </Flex>
          <Text
            type="body"
            content={maxUsableMRs.toLocaleString()}
            weight="bold"
          />
        </Flex>
      </Flex>
      {!isLoading ? (
        <div
          className={`${styles.inputArea} ${warningMessage ? styles.inputWarning : ''}`}
        >
          <Input
            type="number"
            onChange={onInputChange}
            value={rawValue}
            max={maxUsableMRs}
            width={120}
            message={
              warningMessage
                ? {
                    content: warningMessage,
                    type: 'danger',
                    icon: 'information',
                  }
                : undefined
            }
          />
        </div>
      ) : (
        <div className={styles.loadingArea}>
          <LoadingSpinner size="large" />
        </div>
      )}
      <Flex
        justifyContent="space-between"
        width="100%"
        outset={{ top: 'x2' }}
      ></Flex>
      <Flex justifyContent="space-between" width="100%" outset={{ top: 'x2' }}>
        <Button title="Cancel" backgroundColor="#404040" onClick={onCancel} />
        <Button
          title="Apply Membership Rewards"
          isDisabled={isApplyDisabled}
          onClick={() => {
            setIsLoading(true)
            setIsApplyDisabled(true)
            onMRUpdate(amount)
          }}
        />
      </Flex>
    </Flex>
  )
}

export default MembershipRewardsConfigurator
